import React, { useState, useEffect, useRef } from 'react';
import { DataTable, DataTableFilterMeta } from 'primereact/datatable';
import { Column, ColumnBodyOptions } from 'primereact/column';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';

import propertyImportExclusionsService from '../../service/api/PropertyImportExclusionsService';
import { Dialog } from 'primereact/dialog';
import PropertyImportExclusionDialog from './PropertyImportExclusionDialog';
import { Trans, useTranslation } from 'react-i18next';
import { PropertyImportExclusion } from '../../types/PropertyImportExclusion';
import { TOAST_LIFETIME } from '../../settings';

import { FilterMatchMode, FilterOperator } from 'primereact/api';

export const PropertyImportExclusionManagement = () => {
  const toast = useRef<any>();
  const [globalFilter, setGlobalFilter] = useState('');
  const [loading, setLoading] = useState(true);

  const [propertyImportExclusions, setPropertyImportExclusions] = useState<PropertyImportExclusion[]>(null);
  const [propertyImportExclusionToDelete, setPropertyImportExclusionToDelete] = useState<PropertyImportExclusion>();

  const [selectedRowToEdit, setSelectedRowToEdit] = useState<PropertyImportExclusion>(null);
  const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
  const [addEditDialogVisible, setAddEditDialogVisible] = useState(false);
  const [addEditDialogReadOnly, setAddEditDialogReadOnly] = useState(false);
  const { t } = useTranslation();

  const showSaveToast = (summary: string) => {
    toast.current.show({
      severity: 'success',
      summary: summary,
      detail: t('propertyImportExclusions.toast_saved'),
      life: TOAST_LIFETIME,
    });
  };

  const showAddToast = (summary: string) => {
    toast.current.show({
      severity: 'success',
      summary: summary,
      detail: t('propertyImportExclusions.toast_added'),
      life: TOAST_LIFETIME,
    });
  };

  const showDeleteToast = (summary: string) => {
    toast.current.show({
      severity: 'info',
      summary: summary,
      detail: t('propertyImportExclusions.toast_deleted'),
      life: TOAST_LIFETIME,
    });
  };

  const DEFAULT_FILTERS = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },

    name: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
  };

  const [filters, setFilters] = useState<DataTableFilterMeta>(DEFAULT_FILTERS);

  const initFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setGlobalFilter('');
  };

  const clearFilter = () => {
    initFilters();
  };

  const loadData = async () => {
    setLoading(true);
    const propertyImportExclusionsResponse = await propertyImportExclusionsService.getAll();

    const data = propertyImportExclusionsResponse;

    setPropertyImportExclusions(data);
    setLoading(false);
  };

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onGlobalFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value;
    setGlobalFilter(value);
    const filters1 = { ...filters };
    (filters1['global'] as any).value = value;
    setFilters(filters1);
  };

  const tableHeader = (
    <div className="table-header">
      {t('propertyImportExclusions.header')}
      <span>
        <Button
          icon="pi pi-filter-slash"
          className="p-button-outlined mr-2"
          label={t('common.clear')}
          onClick={clearFilter}
        />
        <span className="p-input-icon-left">
          <i className="pi pi-search" />
          <InputText value={globalFilter} onChange={onGlobalFilterChange} placeholder={t('common.search')} />
          <Button
            type="button"
            icon="pi pi-plus"
            label={t('common.add')}
            className="p-button ml-2"
            onClick={() => {
              addPropertyImportExclusion();
            }}
          ></Button>
        </span>
      </span>
    </div>
  );

  const closeAddEditModal = () => {
    setAddEditDialogVisible(false);
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    loadData();
  };

  const confirmDeletePropertyImportExclusion = (propertyImportExclusion: PropertyImportExclusion) => {
    setPropertyImportExclusionToDelete(propertyImportExclusion);
    setDeleteDialogVisible(true);
  };

  const deletePropertyImportExclusion = async () => {
    setLoading(true);
    await propertyImportExclusionsService.delete(propertyImportExclusionToDelete.id);
    showDeleteToast(propertyImportExclusionToDelete.name);

    await loadData();

    setDeleteDialogVisible(false);
  };

  const hideDeleteDialog = () => {
    setDeleteDialogVisible(false);
  };

  const editPropertyImportExclusion = (_propertyImportExclusion: PropertyImportExclusion) => {
    setSelectedRowToEdit(_propertyImportExclusion);
    setAddEditDialogReadOnly(false);
    setAddEditDialogVisible(true);
  };

  const addPropertyImportExclusion = () => {
    setSelectedRowToEdit(null);
    setAddEditDialogReadOnly(false);
    setAddEditDialogVisible(true);
  };

  const deleteDialogFooter = (
    <>
      <Button label={t('common.no')} icon="pi pi-times" className="p-button-text" onClick={() => hideDeleteDialog()} />
      <Button
        label={t('common.yes')}
        icon="pi pi-check"
        className="p-button-text"
        onClick={() => deletePropertyImportExclusion()}
      />
    </>
  );

  const bodyTemplate = (data: PropertyImportExclusion, props: ColumnBodyOptions) => {
    return <>{data[props.field]}</>;
  };

  const actionTemplate = (data: PropertyImportExclusion) => (
    <span>
      <Button
        type="button"
        className="p-button-success mr-2"
        icon="pi pi-pencil"
        tooltip={t('common.edit')}
        tooltipOptions={{ position: 'top' }}
        onClick={() => {
          editPropertyImportExclusion(data);
        }}
      />

      <Button
        type="button"
        className="p-button-success"
        icon="pi pi-trash"
        tooltip={t('common.delete')}
        tooltipOptions={{ position: 'top' }}
        onClick={() => {
          confirmDeletePropertyImportExclusion(data);
        }}
      />
    </span>
  );

  return (
    <div className="grid crud-demo">
      <div className="col-12">
        <div className="card">
          <DataTable
            value={propertyImportExclusions}
            paginator
            className="p-datatable-customers"
            rows={10}
            dataKey="id"
            rowHover
            emptyMessage={t('propertyImportExclusions.empty_message')}
            loading={loading}
            header={tableHeader}
            filterDisplay="menu"
            filters={filters}
            onFilter={(e) => {
              setFilters(e.filters);
            }}
          >
            <Column
              field="name"
              header={t('propertyImportExclusions.name')}
              sortable
              body={bodyTemplate}
              filter
              showFilterMatchModes={false}
              showFilterMenuOptions={false}
            ></Column>
            <Column
              headerStyle={{ width: '8rem' }}
              style={{ minWidth: '8rem' }}
              header={t('common.actions')}
              align="center"
              body={actionTemplate}
              frozen
              alignFrozen="right"
            ></Column>
          </DataTable>
          <PropertyImportExclusionDialog
            dialogVisible={addEditDialogVisible}
            closeAddEditModal={closeAddEditModal}
            readOnly={addEditDialogReadOnly}
            propertyImportExclusion={selectedRowToEdit}
            showAddToast={showAddToast}
            showSaveToast={showSaveToast}
          />

          <Dialog
            visible={deleteDialogVisible}
            className="p-fluid"
            header={
              <span style={{ display: 'flex', alignItems: 'center' }}>
                <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '2rem' }} />
                {t('common.confirm')}
              </span>
            }
            modal
            footer={deleteDialogFooter}
            onHide={hideDeleteDialog}
            breakpoints={{ '896px': '90vw' }}
            style={{ minWidth: '450px' }}
          >
            <div className="confirmation-content">
              {propertyImportExclusionToDelete && (
                <span>
                  <Trans
                    i18nKey="common.confirm_delete_message"
                    values={{ name: propertyImportExclusionToDelete.name }}
                    components={[<strong />]}
                  />
                </span>
              )}
            </div>
          </Dialog>
        </div>
      </div>
      <Toast ref={toast} />
    </div>
  );
};
